import React, { useState, useEffect, useRef } from "react";
import { apiService, resolveLoginUser } from "../services/api";
import { useAppSelector } from "../state/hooks";
import { Chart, registerables } from "chart.js";

// Register Chart.js components
Chart.register(...registerables);

// Expose Chart to window for injected scripts
(window as any).Chart = Chart;

export const Dashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"hr" | "operations">("operations");
  const [dashboardHtml, setDashboardHtml] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  //const [isStabilizing, setIsStabilizing] = useState<boolean>(false);
  const [showContent, setShowContent] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const authBranchId = useAppSelector((state) => state.auth.user?.branch_id);
  const layoutBranchId = useAppSelector(
    (state) => state.layout.currentBranch?.id
  );

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      setError(null);
      setShowContent(false);
      try {
        const reportName =
          activeTab === "hr" ? "dash_board" : "operations_dashboard";

        const loginUser = resolveLoginUser();
        const branchId = String(
          loginUser?.branch_id || authBranchId || layoutBranchId || ""
        ).trim();
        const response = await apiService.request({
          endpoint: `/pabreport/report?name=${reportName}`,
          method: "POST",
          payload: {
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            ...(branchId ? { branch_id: branchId } : {}),
            loginUser: {
              ...(loginUser ?? {}),
              ...(branchId ? { branch_id: branchId } : {}),
            },
          },
          includeAuthToken: true
        });

        if (response && response.data) {
          setDashboardHtml(response.data);
        } else if (typeof response === "string" && response.includes("<html")) {
          setDashboardHtml(response);
        } else {
          setError("Invalid response format received from server.");
        }
      } catch (err) {
        console.error("Failed to fetch dashboard:", err);
        setError("⚠️ Failed to load dashboard data. Please check your connection or contact support.");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, [activeTab, authBranchId, layoutBranchId]);

  useEffect(() => {
    if (!loading && dashboardHtml && containerRef.current) {
      // Start stabilization phase
      //setIsStabilizing(true);
      setShowContent(false);

      // Clear existing content
      containerRef.current.innerHTML = "";

      // Create a temporary container to parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(dashboardHtml, "text/html");

      // Extract and inject styles
      const styles = doc.querySelectorAll("style");
      styles.forEach((style) => {
        const styleTag = document.createElement("style");
        styleTag.textContent = style.textContent;
        styleTag.className = "dynamic-dashboard-style";
        document.head.appendChild(styleTag);
      });

      // Inject body content
      const contentWrapper = document.createElement("div");
      contentWrapper.innerHTML = doc.body.innerHTML;
      containerRef.current.appendChild(contentWrapper);

      // Extract and execute scripts
      const scripts = doc.querySelectorAll("script");

      // Execute scripts after a short delay to ensure DOM elements are placed
      const timer = setTimeout(() => {
        scripts.forEach((script) => {
          const newScript = document.createElement("script");
          newScript.className = "dynamic-dashboard-script";
          if (script.src) {
            newScript.src = script.src;
            newScript.async = false;
          } else {
            newScript.textContent = script.textContent;
          }
          document.body.appendChild(newScript);
        });
      }, 1);

      // Stabilization delay - hide the "glitchy" initial render period
      const stabilizationTimer = setTimeout(() => {
        //setIsStabilizing(false);
        // Small additional delay before fade-in to ensure layout is stable
        setTimeout(() => setShowContent(true), 50);
      }, 1);

      // Cleanup function to remove injected styles and scripts
      return () => {
        clearTimeout(timer);
        clearTimeout(stabilizationTimer);
        document.querySelectorAll(".dynamic-dashboard-style").forEach(el => el.remove());
        document.querySelectorAll(".dynamic-dashboard-script").forEach(el => el.remove());
      };
    }
  }, [dashboardHtml, loading]);

  return (
    <div className="bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-200">
      <div className="p-4">
        {/* Tabs */}
        <div className="mb-4 flex gap-2 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => {
              setActiveTab("operations");
              // Focus first searchable dropdown input after tab switch
              setTimeout(() => {
                const firstSearchableDropdownInput = document.querySelector('[data-searchable-dropdown-input="true"]:not([disabled])') as HTMLInputElement;
                if (firstSearchableDropdownInput) {
                  firstSearchableDropdownInput.focus();
                }
              }, 100);
            }}
            className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === "operations"
              ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              }`}
          >
            Operations dashboard
          </button>
          <button
            onClick={() => {
              setActiveTab("hr");
              // Focus first searchable dropdown input after tab switch
              setTimeout(() => {
                const firstSearchableDropdownInput = document.querySelector('[data-searchable-dropdown-input="true"]:not([disabled])') as HTMLInputElement;
                if (firstSearchableDropdownInput) {
                  firstSearchableDropdownInput.focus();
                }
              }, 100);
            }}
            className={`px-4 py-2 font-medium text-sm transition-colors ${activeTab === "hr"
              ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
              : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
              }`}
          >
            HR dashboard
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center p-20 gap-4">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 dark:border-blue-400 border-t-transparent"></div>
            <p className="text-gray-500 dark:text-gray-400 font-medium animate-pulse">Loading dashboard...</p>
          </div>
        ) : error ? (
          <div className="p-4 text-red-500 dark:text-red-400 text-center font-semibold bg-white dark:bg-gray-800 rounded-lg border border-red-100 dark:border-red-900/50">
            {error}
          </div>
        ) : (
          <div className="relative">
            {/* stabilization loader - shows while charts and styles are settling */}
            {false && (
              <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-20 gap-4 bg-gray-50/80 dark:bg-gray-900/80 backdrop-blur-[1px]">
                <div className="animate-spin rounded-full h-10 w-10 border-2 border-blue-400 dark:border-blue-500 border-t-transparent"></div>
                <p className="text-xs text-gray-400 dark:text-gray-500 font-medium">Finalizing view...</p>
              </div>
            )}

            <div
              ref={containerRef}
              className={`dynamic-dashboard-container transition-all duration-700 ease-in-out ${showContent ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
                }`}
            />
          </div>
        )}
      </div>
    </div>
  );
};
